import {
  Card,
  CardContent,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material"
import theme from "@skistarab/mui-theme"
import { useTranslation } from "react-i18next"

import { useSelectedResort } from "@/hooks"
import useDateLocale from "@/hooks/useDateLocale"
import useProductTotals from "@/hooks/useProductTotals"
import { useDateParam } from "@/hooks/useSkipassFlow"
import { formatStartDate } from "@/utils/dateUtils"

import { SummaryRow, SummaryGrid } from "../SummaryGrid"

type OrderSummaryCardProps = {
  bookingLengthName?: string
  destinationName?: string
  startDateValue?: string
}

const OrderSummaryCard = ({
  bookingLengthName,
  destinationName,
  startDateValue,
}: OrderSummaryCardProps) => {
  const { t } = useTranslation()
  const locale = useDateLocale()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"))

  const padding = isDesktop ? 4 : 2
  const borderRadius = isMobile ? 0 : 2

  const { startDate } = useDateParam()
  const { selectedResort } = useSelectedResort()
  const { totalQuantity } = useProductTotals()

  const destination = destinationName ?? selectedResort?.name ?? ""
  const startDateDisplay = startDateValue ?? formatStartDate(startDate, locale)

  const mobileStyles = isMobile
    ? {
        width: "100vw",
        position: "relative",
        left: "50%",
        transform: "translateX(-50%)",
        boxSizing: "border-box",
      }
    : {}

  return (
    <Card
      sx={{
        minWidth: "100%",
        padding,
        borderRadius,
        ...mobileStyles,
      }}
    >
      <CardContent
        sx={{
          p: 0,
          "&:last-child": { p: 0 },
        }}
      >
        <Stack direction="column" spacing={1}>
          <Typography variant="h6" component="h2" fontWeight="bold">
            {t("orderSummary.title")}
          </Typography>
          <SummaryGrid>
            <SummaryRow
              label={t("orderSummary.destination")}
              value={destination}
            />
            <SummaryRow
              label={t("orderSummary.startDate")}
              value={startDateDisplay}
            />
            <SummaryRow
              label={t("orderSummary.numberOfDays")}
              value={bookingLengthName || ""}
            />
            {totalQuantity > 0 && (
              <SummaryRow
                label={t("orderSummary.numberOfPasses")}
                value={totalQuantity}
              />
            )}
          </SummaryGrid>
        </Stack>
      </CardContent>
    </Card>
  )
}

export default OrderSummaryCard
